/**
 * Расчёт цены за единицу с учётом варианта логотипа и объёмных цен.
 * Порог объёма (для базы товара и для каждого основного варианта логотипа) —
 * суммарное количество по product.id (только основные варианты, без is_addon).
 * Порог доп. услуги (is_addon) — только по строкам, где услуга отмечена чекбоксом.
 */

export function normalizeQuantity(qty) {
  const n = Number(qty)
  if (!Number.isFinite(n) || n <= 0) return 0
  return Math.floor(n)
}

export function isLogoVariantAddon(variant) {
  if (!variant) return false
  const flag = variant.is_addon
  return flag === true || flag === 1 || flag === 'true' || flag === '1'
}

export function splitLogoVariants(variants) {
  const list = Array.isArray(variants) ? variants : []
  return {
    primary: list.filter((v) => !isLogoVariantAddon(v)),
    addons: list.filter((v) => isLogoVariantAddon(v))
  }
}

function hasBulkPricingConfig(bulkConfig) {
  if (!bulkConfig || bulkConfig.is_addon) return false
  return !!(
    bulkConfig?.has_bulk_pricing ||
    (Array.isArray(bulkConfig?.bulk_pricing) && bulkConfig.bulk_pricing.length)
  )
}

export function getActiveBulkTier(bulkConfig, totalQty) {
  const qty = normalizeQuantity(totalQty)
  if (!hasBulkPricingConfig(bulkConfig) || !qty) return null
  const tiers = [...bulkConfig.bulk_pricing].sort((a, b) => a.min_quantity - b.min_quantity)
  let active = null
  for (const t of tiers) {
    if (qty >= normalizeQuantity(t.min_quantity)) active = t
  }
  return active
}

function applyBulkTiers(basePrice, refPrice, bulkConfig, totalQty) {
  const base = Number(basePrice)
  const qty = normalizeQuantity(totalQty)
  if (!hasBulkPricingConfig(bulkConfig) || !Array.isArray(bulkConfig.bulk_pricing) || !bulkConfig.bulk_pricing.length || !qty) {
    return base
  }
  const refBase = Number(refPrice)
  if (!refBase) return base

  const tiers = [...bulkConfig.bulk_pricing].sort((a, b) => a.min_quantity - b.min_quantity)
  let tierPrice = null
  for (const t of tiers) {
    if (qty >= normalizeQuantity(t.min_quantity)) {
      tierPrice = Number(t.unit_price)
    }
  }
  if (tierPrice == null) return base
  return tierPrice
}

function hasLogoAddonBulkConfig(config) {
  return !!(
    config?.has_bulk_pricing ||
    (Array.isArray(config?.bulk_pricing) && config.bulk_pricing.length)
  )
}

export function getLogoAddonConfig(addon) {
  if (!addon) return null
  const bulkPricing = addon.bulk_pricing ? [...addon.bulk_pricing] : []
  return {
    price: Number(addon.price ?? 0),
    has_bulk_pricing: !!(addon.has_bulk_pricing || bulkPricing.length),
    bulk_pricing: bulkPricing
  }
}

export function getLogoAddonConfigFromVariant(variant) {
  return getLogoAddonConfig(variant)
}

export function getSizeBulkConfig(size) {
  if (!size) return null
  const bulkPricing = size.bulk_pricing ? [...size.bulk_pricing] : []
  if (!size.has_bulk_pricing && !bulkPricing.length) return null
  return {
    has_bulk_pricing: !!(size.has_bulk_pricing || bulkPricing.length),
    bulk_pricing: bulkPricing
  }
}

/** Объёмные цены заданы на уровне размеров (флаг товара), а не на уровне product.bulk_pricing. */
export function productUsesSizeLevelBulk(product) {
  return !!(product?.has_sizes && product?.has_bulk_pricing)
}

export function getSizeNextBulkTierInfo(
  product,
  size,
  totalQty,
  variantConfig = null,
  addonConfigs = [],
  addonResolver = null
) {
  const config = getSizeBulkConfig(size)
  if (!config?.bulk_pricing?.length) return null
  const qty = normalizeQuantity(totalQty)
  const sorted = [...config.bulk_pricing].sort((a, b) => a.min_quantity - b.min_quantity)
  const next = sorted.find((t) => normalizeQuantity(t.min_quantity) > qty)
  if (!next) return null
  const nextQty = normalizeQuantity(next.min_quantity)
  return {
    minQuantity: nextQty,
    unitPrice: computeUnitPrice(product, nextQty, variantConfig, addonConfigs, addonResolver, config)
  }
}

export function getSizeBulkConfigFromProduct(product) {
  if (!product) return null
  if (product.size_has_bulk_pricing != null || product.size_bulk_pricing?.length) {
    const bulkPricing = product.size_bulk_pricing ? [...product.size_bulk_pricing] : []
    return {
      has_bulk_pricing: !!(product.size_has_bulk_pricing || bulkPricing.length),
      bulk_pricing: bulkPricing
    }
  }
  if (product.size_id != null && Array.isArray(product.sizes)) {
    const s = product.sizes.find((sz) => Number(sz.id) === Number(product.size_id))
    return getSizeBulkConfig(s)
  }
  return null
}

function applyLogoAddonBulkTiers(basePrice, refPrice, addonConfig, totalQty) {
  const base = Number(basePrice)
  const qty = normalizeQuantity(totalQty)
  if (!hasLogoAddonBulkConfig(addonConfig) || !Array.isArray(addonConfig.bulk_pricing) || !addonConfig.bulk_pricing.length || !qty) {
    return base
  }
  const refBase = Number(refPrice)
  if (!refBase) return base

  const tiers = [...addonConfig.bulk_pricing].sort((a, b) => a.min_quantity - b.min_quantity)
  let tierPrice = null
  for (const t of tiers) {
    if (qty >= normalizeQuantity(t.min_quantity)) {
      tierPrice = Number(t.unit_price)
    }
  }
  if (tierPrice == null) return base
  return tierPrice
}

export function computeLogoAddonUnitPrice(addonConfig, totalProductQty) {
  if (!addonConfig) return 0
  const base = Number(addonConfig.price)
  return applyLogoAddonBulkTiers(base, base, addonConfig, totalProductQty)
}

/** Сумма надбавок доп. услуг. volumeQtyOrResolver — число или (addon) => qty для порога. */
export function computeFlatAddonsUnitPrice(addonConfigs = [], volumeQtyOrResolver = 0) {
  if (!Array.isArray(addonConfigs) || !addonConfigs.length) return 0
  const resolveQty = typeof volumeQtyOrResolver === 'function'
    ? volumeQtyOrResolver
    : () => normalizeQuantity(volumeQtyOrResolver)
  return addonConfigs.reduce(
    (sum, a) => sum + computeLogoAddonUnitPrice(getLogoAddonConfig(a) || a, resolveQty(a)),
    0
  )
}

/** Цена за шт. до первого объёмного порога (для зачёркнутой «старой» цены). */
export function getVariantReferenceUnitPrice(product, variantConfig, addonConfigs = []) {
  if (!product || !variantConfig) return 0
  if (!hasBulkPricingConfig(variantConfig)) {
    return computeUnitPrice(product, 0, variantConfig, addonConfigs)
  }
  const sorted = [...variantConfig.bulk_pricing].sort((a, b) => a.min_quantity - b.min_quantity)
  const firstMin = normalizeQuantity(sorted[0]?.min_quantity)
  const refQty = firstMin > 1 ? firstMin - 1 : 0
  return computeUnitPrice(product, refQty, variantConfig, addonConfigs)
}

export function computeProductBaseUnitPrice(product, totalProductQty, sizeBulkConfig = null) {
  if (!product) return 0
  const base = Number(product.price)
  if (sizeBulkConfig && hasBulkPricingConfig(sizeBulkConfig)) {
    return applyBulkTiers(base, base, sizeBulkConfig, totalProductQty)
  }
  // При объёмных ценах по размерам product.bulk_pricing не используется (устаревшие данные).
  if (productUsesSizeLevelBulk(product)) {
    return base
  }
  return applyBulkTiers(base, base, product, totalProductQty)
}

export function computeVariantAddonUnitPrice(variantConfig, totalProductQty) {
  if (!variantConfig || variantConfig.is_addon) return 0
  const base = Number(variantConfig.price)
  return applyBulkTiers(base, base, variantConfig, totalProductQty)
}

export function computeUnitPrice(
  product,
  totalProductQty,
  variantConfig = null,
  addonConfigs = [],
  addonVolumeQtyResolver = null,
  sizeBulkConfig = null
) {
  const productPart = computeProductBaseUnitPrice(product, totalProductQty, sizeBulkConfig)
  const variantPart = variantConfig && !variantConfig.is_addon
    ? computeVariantAddonUnitPrice(variantConfig, totalProductQty)
    : 0
  const addonsPart = addonConfigs.length
    ? computeFlatAddonsUnitPrice(addonConfigs, addonVolumeQtyResolver || (() => 0))
    : 0
  return productPart + variantPart + addonsPart
}

export function getLogoAddonsFromProduct(product) {
  if (!product?.logo_addons?.length) return []
  return product.logo_addons.map((a) => ({
    id: a.id,
    name: a.name,
    ...getLogoAddonConfig(a)
  }))
}

function logoAddonsKey(addons) {
  if (!addons?.length) return ''
  return addons
    .map((a) => a.id)
    .filter((id) => id != null)
    .sort((a, b) => Number(a) - Number(b))
    .join('-')
}

export function getCartItemKey(product) {
  const parts = [product.id, product.size_id, product.logo_variant_id]
  const addonPart = logoAddonsKey(getLogoAddonsFromProduct(product))
  if (addonPart) parts.push(addonPart)
  return parts.filter((v) => v != null && v !== '').join('_')
}

export function formatVariantLabelWithAddons(variantLabel, addons) {
  const names = []
  if (variantLabel) names.push(variantLabel)
  if (addons?.length) {
    addons.forEach((a) => {
      if (a.name && !names.includes(a.name)) names.push(a.name)
    })
  }
  return names.length ? names.join(', ') : null
}

export function formatCartItemSuffix(item) {
  const parts = []
  const size = item.size_info || item.product?.size_info
  const variant = formatVariantLabelWithAddons(
    item.logo_variant_info || item.product?.logo_variant_info,
    item.product?.logo_addons || item.logo_addons
  )
  if (size) parts.push(size)
  if (variant) parts.push(variant)
  return parts.length ? ` (${parts.join(', ')})` : ''
}

export function formatCartItemName(item) {
  const name = item.product?.name || ''
  return `${name}${formatCartItemSuffix(item)}`
}

export function getCartItemImage(item) {
  const variantImage = item.logo_variant_image || item.product?.logo_variant_image
  if (variantImage) return variantImage
  const images = item.product?.images
  if (images?.length) return images[0].image_path
  return null
}

export function getVariantConfigFromProduct(product) {
  if (product.logo_variant_id == null && product.logo_variant_price == null) return null

  if (product.logo_variant_price != null || product.logo_variant_has_bulk_pricing != null) {
    return {
      price: Number(product.logo_variant_price ?? 0),
      has_bulk_pricing: !!product.logo_variant_has_bulk_pricing,
      bulk_pricing: product.logo_variant_bulk_pricing ? [...product.logo_variant_bulk_pricing] : [],
      is_addon: false
    }
  }

  if (product.logo_variant_id && Array.isArray(product.logo_variants)) {
    const v = product.logo_variants.find(
      (lv) => Number(lv.id) === Number(product.logo_variant_id) && !isLogoVariantAddon(lv)
    )
    if (v) {
      return getVariantConfigFromVariant(v)
    }
  }

  return null
}

export function getVariantConfigFromVariant(variant) {
  if (!variant || isLogoVariantAddon(variant)) return null
  const bulkPricing = variant.bulk_pricing ? [...variant.bulk_pricing] : []
  return {
    price: Number(variant.price),
    has_bulk_pricing: !!(variant.has_bulk_pricing || bulkPricing.length),
    bulk_pricing: bulkPricing,
    is_addon: false
  }
}

export function snapshotLogoAddons(addons) {
  if (!addons?.length) return []
  return addons.map((a) => {
    const config = getLogoAddonConfig(a)
    return {
      id: a.id,
      name: a.name,
      price: config.price,
      has_bulk_pricing: config.has_bulk_pricing,
      bulk_pricing: config.bulk_pricing
    }
  })
}

/** @deprecated use getVariantConfigFromProduct */
export function getVariantPriceFromProduct(product) {
  const config = getVariantConfigFromProduct(product)
  return config ? config.price : null
}

export function buildGroupQty(items) {
  return items.reduce((acc, item) => {
    const pid = item.product.id
    acc[pid] = (acc[pid] || 0) + normalizeQuantity(item.quantity)
    return acc
  }, {})
}

/** Количество по product.id и addon.id только для строк с отмеченной доп. услугой. */
export function buildAddonGroupQty(items) {
  return items.reduce((acc, item) => {
    const pid = item.product.id
    const addons = item.product?.logo_addons || item.logo_addons || []
    const qty = normalizeQuantity(item.quantity)
    if (!addons.length || !qty) return acc
    if (!acc[pid]) acc[pid] = {}
    for (const addon of addons) {
      const aid = Number(addon.id)
      if (!Number.isFinite(aid)) continue
      acc[pid][aid] = (acc[pid][aid] || 0) + qty
    }
    return acc
  }, {})
}

export function getProductAddonVolumeResolver(productId, items) {
  const map = buildAddonGroupQty(items)[productId] || {}
  return (addon) => normalizeQuantity(map[Number(addon.id)] || 0)
}

export function buildVariantGroupQty(items) {
  return items.reduce((acc, item) => {
    if (item.product.logo_variant_id == null) return acc
    const pid = item.product.id
    acc[pid] = (acc[pid] || 0) + item.quantity
    return acc
  }, {})
}

export function getProductCartSummaryLines(items, product, groupQty) {
  if (!product?.id) return []
  const totalQ = groupQty?.[product.id] ?? buildGroupQty(items)[product.id] ?? 0

  const addonResolver = getProductAddonVolumeResolver(product.id, items)

  return items
    .filter((item) => item.product.id === product.id)
    .map((item) => {
      const variantConfig = getVariantConfigFromProduct(item.product)
      const addonConfigs = getLogoAddonsFromProduct(item.product)
      const sizeBulkConfig = getSizeBulkConfigFromProduct(item.product)
      const unitPrice = computeUnitPrice(
        item.product,
        totalQ,
        variantConfig,
        addonConfigs,
        addonResolver,
        sizeBulkConfig
      )
      const sizeLabel = item.size_info || item.product.size_info || null
      const variantLabel = formatVariantLabelWithAddons(
        item.logo_variant_info || item.product.logo_variant_info,
        addonConfigs
      )
      return {
        sizeLabel,
        variantLabel,
        quantity: item.quantity,
        unitPrice,
        lineTotal: unitPrice * item.quantity
      }
    })
}

export function formatOrderItemName(item) {
  const name = item.product?.name || 'Товар не найден'
  const parts = []
  if (item.size_info) parts.push(item.size_info)
  const variantPart = formatVariantLabelWithAddons(
    item.logo_variant_info,
    item.product?.logo_addons || item.logo_addons
  )
  if (variantPart) parts.push(variantPart)
  return parts.length ? `${name} (${parts.join(', ')})` : name
}

export function formatOrderItemLogo(item) {
  const fromOrderItem = item.logo_variant_info?.trim()
  if (fromOrderItem) return fromOrderItem

  const product = item.product
  if (!product) return null

  const addons = product.logo_addons || item.logo_addons
  const fromProductVariant = product.logo_variant_info?.trim()
  if (fromProductVariant) {
    return formatVariantLabelWithAddons(fromProductVariant, addons)
  }

  const addonsOnly = formatVariantLabelWithAddons(null, addons)
  if (addonsOnly) return addonsOnly

  return product.logo?.name?.trim() || null
}

export function getOrderItemImage(item) {
  if (item.logo_variant_image) return item.logo_variant_image
  const images = item.product?.images
  if (images?.length) return images[0].image_path
  return null
}

/** Текст варианта логотипа для заказа (основной + доп. услуги). */
export function formatLogoVariantInfoForOrder(variantInfo, logoAddons) {
  return formatVariantLabelWithAddons(variantInfo, logoAddons) || variantInfo || null
}
